import * as _ from 'lodash';
import {WB_ProductConditionTypeRow} from "../PA.Core/PAdbSchemaRows";
import {PAdbContext} from "../PA.Core/PAdbContext";
import * as AsyncLock from 'async-lock';
import {esWB_Product_Condition_Type} from "../PA.Core/PAdbSchema";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import {GenericLease} from "../PA.Utils/GenericLease";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";

export class ConditionTypeService {

    private static _dbContext: PAdbContext;
    private static _lease: ILease;

    private static dctCndTypeByWBId: { [id: number]: WB_ProductConditionTypeRow } = {};
    private static dctCndTypeByIDSetID: { [id: string]: WB_ProductConditionTypeRow } = {};

    private static dctLock: any = new AsyncLock();
    private static bIsLoaded: boolean = false;

    static getConditionTypeByWBID(conditionTypeWBID: number): Promise<WB_ProductConditionTypeRow> {

        return new Promise<WB_ProductConditionTypeRow>((resolve, error) => {
            ConditionTypeService.dctLock.acquire('global',
                (done: any) => {
                    ConditionTypeService.checkLease()
                        .then((ignore) => {
                            if (conditionTypeWBID in ConditionTypeService.dctCndTypeByWBId) {
                                done(null, ConditionTypeService.dctCndTypeByWBId[conditionTypeWBID]);
                                resolve(ConditionTypeService.dctCndTypeByWBId[conditionTypeWBID]);
                                return;
                            } else if (ConditionTypeService.bIsLoaded && !(conditionTypeWBID in ConditionTypeService.dctCndTypeByWBId)) {
                                done(null, ConditionTypeService.dctCndTypeByWBId[conditionTypeWBID]);
                                resolve(undefined);
                                return;
                            }

                            return ConditionTypeService.loadFromDB();
                        })
                        .then((res) => {
                            done(null, ConditionTypeService.dctCndTypeByWBId[conditionTypeWBID]);
                            return ConditionTypeService.dctCndTypeByWBId[conditionTypeWBID];
                        })
                        .then((targetType) => {
                            resolve(targetType);
                        })
                        .catch((err2) => {
                            error(err2);
                        });
                }
            );
        });
    }

    static getConditionTypeByCndTypeSrcID(cndType: string, sourceID: string): Promise<WB_ProductConditionTypeRow> {
        return new Promise<WB_ProductConditionTypeRow>((resolve, error) => {

            let dctKey: string = cndType + sourceID;

            ConditionTypeService.dctLock.acquire('global',
                (done: any) => {
                    ConditionTypeService.checkLease()
                        .then((ignore) => {
                            if (dctKey in ConditionTypeService.dctCndTypeByIDSetID) {
                                done(null, ConditionTypeService.dctCndTypeByIDSetID[dctKey]);
                                resolve(ConditionTypeService.dctCndTypeByIDSetID[dctKey]);
                                return;
                            } else if (ConditionTypeService.bIsLoaded && !(dctKey in ConditionTypeService.dctCndTypeByIDSetID)) {
                                done(null, ConditionTypeService.dctCndTypeByIDSetID[dctKey]);
                                resolve(undefined);
                                return;
                            }

                            return ConditionTypeService.loadFromDB();
                        })
                        .then((res) => {
                            done(null, ConditionTypeService.dctCndTypeByIDSetID[dctKey]);
                            return ConditionTypeService.dctCndTypeByIDSetID[dctKey];
                        })
                        .then((targetType) => {
                            resolve(targetType);
                        })
                        .catch((err2) => {
                            error(err2);
                        });
                }
            );

        });
    }

    static async getConditionTypesByWbIDs(wbCndTypeIDs: number[]): Promise<WB_ProductConditionTypeRow[]> {

        let retVal: WB_ProductConditionTypeRow[] = [];
        for (let anID of wbCndTypeIDs) {
            let aCndType: WB_ProductConditionTypeRow = await ConditionTypeService.getConditionTypeByWBID(anID);
            if (!_.isUndefined(aCndType)) {
                retVal.push(aCndType);
            }
        }

        return retVal;
    }

    static async getConditionTypesByCndTypeIDsSrcID(cndTypes: string[], sourceID: string): Promise<WB_ProductConditionTypeRow[]> {

        let retVal: WB_ProductConditionTypeRow[] = [];
        for (let anID of cndTypes) {
            let aCndType = await ConditionTypeService.getConditionTypeByCndTypeSrcID(anID, sourceID);
            if (!_.isUndefined(aCndType)) {
                retVal.push(aCndType);
            }
        }
        return retVal;

    }

    private static async getCndTypesByCndTypesSrcID(cndTypes: string[], sourceID: string, retVal: WB_ProductConditionTypeRow[]) {
        for (let anID of cndTypes) {
            let aCndType = await ConditionTypeService.getConditionTypeByCndTypeSrcID(anID, sourceID);
            if (!_.isUndefined(aCndType)) {
                retVal.push(aCndType);
            }
        }
    }

    private static async checkLease(): Promise<void> {
        if (_.isUndefined(ConditionTypeService._lease)) {
            ConditionTypeService._lease = new GenericLease(LeaseRegistry.Lease_Type_ConditionContext);
            ConditionTypeService._lease.forceExpire();
        }

        if (await ConditionTypeService._lease.hasDataChanged()) {
            await ConditionTypeService.reset();
            ConditionTypeService._lease.resetLease();
        }
    }

    private static loadFromDB(): Promise<void> {
        return new Promise<void>((resolve, error) => {

            ConditionTypeService.bIsLoaded = false;

            if (_.isUndefined(ConditionTypeService._dbContext)) {
                ConditionTypeService.instantiateDbContext();
            }

            ConditionTypeService._dbContext.findAll(esWB_Product_Condition_Type, {})
                .then((conditions) => {
                    if (_.isArray(conditions)) {
                        let rows = conditions as WB_ProductConditionTypeRow[];

                        _.forEach(rows, (row) => {
                            ConditionTypeService.dctCndTypeByWBId[row.cif.PRD_CNDM_ID] = row;
                            ConditionTypeService.dctCndTypeByIDSetID[row.cif.CONDITION_TYPE + row.cif.SOURCE_ID] = row;
                        });
                    }
                    ConditionTypeService.bIsLoaded = true;
                    resolve();
                })
                .catch((err) => {
                    error(err);
                });

        });
    }

    static reset() {
        ConditionTypeService.dctCndTypeByIDSetID = {};
        ConditionTypeService.dctCndTypeByWBId = {};
        ConditionTypeService.bIsLoaded = false;
    }

    private static instantiateDbContext() {
        ConditionTypeService._dbContext = new PAdbContext();
    }
}

